import { FC, useEffect, useMemo, useState } from 'react'

import { SummarySectionPreset } from 'components/features/rewards/rewardPageSummary/SummarySectionPreset/SummarySectionPreset'
import { formatUSCurrency } from 'helpers/formatHelpers'
import styles from 'components/features/rewards/rewardPageSummary/LeftToInvest/LeftToInvest.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import RadialProgressbar from 'components/features/pfm/RewardSummary/RadialProgressbar'
import { minRedemptionAmountSelector, usdBalanceSelector } from 'selectors/bountySelectors'
import { Subject, timer } from 'rxjs'
import { scan, takeUntil, takeWhile } from 'rxjs/operators'
import cn from 'classnames'

const DELAY_TIMEOUT = 1000
const MIN_STEP_TIMEOUT = 1
const STEP_TIMEOUT_DIVIDEND = 500

type LeftToInvestProps = {
}

export const LeftToInvest: FC<LeftToInvestProps> = () => {
  const cashBalance = useStoreSelector(usdBalanceSelector)
  const minRedemption = useStoreSelector(minRedemptionAmountSelector)
  if (cashBalance === null || minRedemption === null) {
    return null
  }

  return (
    <LeftToInvestInner balance={ cashBalance } minRedemption={ minRedemption } />
  )
}

type LeftToInvestInnerProps = {
  balance: number
  minRedemption: number
}
const LeftToInvestInner: FC<LeftToInvestInnerProps> = (props) => {
  const { balance, minRedemption } = props

  const header = useMemo(() => {
    const usdLeftForInvesting = Math.max(0, minRedemption - balance)

    return (
      <h3 className={ styles.headerLabel }>
        You’re { formatUSCurrency(usdLeftForInvesting, usdLeftForInvesting < 1 ? 'penny' : 'dollar') } away from investing or redeeming!
      </h3>
    )
  }, [minRedemption, balance])

  const [animationStep, setAnimationStep] = useState(0)

  const maxAnimationSteps = useMemo(() => Math.ceil(balance), [balance])

  useEffect(() => {
    const destroySubject = new Subject()
    const interval = Math.max(MIN_STEP_TIMEOUT, STEP_TIMEOUT_DIVIDEND / balance)

    timer(DELAY_TIMEOUT, interval).pipe(
      scan((_acc, _item, index) => index, 0),
      takeUntil(destroySubject),
      takeWhile((index) => index < maxAnimationSteps + 2),
    ).subscribe(setAnimationStep)

    return () => {
      destroySubject.next(null)
    }
  }, [balance, maxAnimationSteps])

  const main = useMemo(() => {
    const displayBalance = Math.min(balance, animationStep + 1)
    const percent = Math.min(1, displayBalance / minRedemption)

    const balansClassName = cn(
      styles.labelCenterBalanceCurrent,
      animationStep > maxAnimationSteps && styles.emphasized,
      displayBalance.toFixed(2).length > 3 && styles.shrinked,
    )
    return (
      <div className={ styles.chartContainer }>
        <div className={ styles.progressbar }>
          <div className={ styles.progressbarWrapper }>
            <RadialProgressbar value={ percent } />
          </div>
          <h5 className={ styles.labelHeader }>
            <span className={ styles.chartLabel }>Current<br />Balance</span>
          </h5>
          <div className={ balansClassName }>
            <div className={ styles.currency }>
              $
            </div>
            <div className={ cn(styles.amount, (animationStep !== 0 || maxAnimationSteps <= 1) && styles.visible) }>
              { displayBalance }
            </div>
          </div>
          <p className={ styles.labelBalanceCurrent }>{ formatUSCurrency(0, 'dollar') }</p>
          <p className={ styles.labelBalanceRequired }>{ formatUSCurrency(minRedemption, 'dollar') }</p>
        </div>
      </div>
    )
  }, [minRedemption, balance, animationStep, maxAnimationSteps])

  return (
    <SummarySectionPreset
      header={ header }
      main={ main }
      footer={ null }
      className={ styles.container }
    />
  )
}
